/**
 * 수업기록·평가기록·행동특성 모달의 AI 피드백 (플랫폼 마스터 관리자 전용).
 * - 생성: 학생 이름을 요청마다 새 코드로 가려 /api/ai-record-feedback 호출 → 결과를 기록 메모와 같은
 *   `#m:{memberId}` 태그 텍스트 하나로 변환(칩으로 표시)
 * - 편집: 메모와 같은 ClassRecordMemoNoteEditor 하나
 * - 읽기/쓰기: 학급 단위 React Query + localStorage 캐시(useRecordAiFeedbacksQuery),
 *   부모 모달의 확인/저장 시 ref.persist(), 휴지통 삭제 시 ref.remove()
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import AiSparkIcon from '../common/AiSparkIcon';
import { useAiAccess } from '../../hooks/useAiAccess';
import type { ClassMemberRow } from '../../utils/classMembersStorage';
import ClassRecordMemoNoteEditor from './ClassRecordMemoNoteEditor';
import {
  anonymizeRecord,
  requestAiRecordFeedback,
  clipFeedbackText,
  type AiRecordFeedbackKind,
} from '../../utils/aiRecordFeedback';
import { buildMemberTagRaw } from '../../utils/classRecordMemoHashtags';
import {
  deleteRecordAiFeedback,
  gradeIdFromRecordKey,
  saveRecordAiFeedback,
} from '../../services/recordAiFeedbackService';
import { useRecordAiFeedbacksQuery } from '../../query/hooks/useRecordAiFeedbacksQuery';

export type AiRecordFeedbackPanelHandle = {
  /** 편집된 피드백 저장(변경이 있을 때만). 비어 있으면 삭제 */
  persist: () => Promise<void>;
  /** 현재 편집본을 캡처해 두고, 기록 저장 성공 후 호출할 저장 함수 반환(모달이 닫혀도 동작) */
  capture: () => () => Promise<void>;
  /** 이 기록 셀의 피드백 삭제 */
  remove: (kinds?: AiRecordFeedbackKind[]) => Promise<void>;
  /** 기록 내장용: 현재 표시 중인 피드백 텍스트(편집본 우선). 기록 저장 시 함께 직렬화 */
  getEmbedValue: () => string;
};

type Props = {
  kind: AiRecordFeedbackKind;
  note: string;
  contextLines?: Array<string | null | undefined>;
  members: ClassMemberRow[];
  allMembers?: ClassMemberRow[];
  disabled?: boolean;
  workspaceId?: string;
  recordKey?: string;
  /**
   * 기록에 내장된 피드백(수업·평가기록). undefined 면 내장 미지원(행동특성) → 테이블 사용.
   * 내장 모드에서는 내장값이 원본, record_ai_feedbacks 는 기존 데이터 읽기 폴백.
   */
  embeddedText?: string | null;
  /** 피드백 편집본이 저장본과 달라졌는지 알림(저장 버튼 활성화용) */
  onDirtyChange?: (dirty: boolean) => void;
};

/** 이용권별 권한(AI 행동특성 피드백 기능)으로 AI 피드백 노출 여부를 판단. 관리자는 항상 허용. */
function useIsMasterAdmin(): boolean {
  return useAiAccess().canPlan('ai_record_feedback');
}

const Inner = forwardRef<AiRecordFeedbackPanelHandle, Props>(function AiRecordFeedbackPanelInner(
  { kind, note, contextLines = [], members, allMembers, disabled, workspaceId = '', recordKey = '', embeddedText, onDirtyChange },
  ref
) {
  const gradeId = gradeIdFromRecordKey(recordKey);
  const { data: rows } = useRecordAiFeedbacksQuery({ workspaceId, gradeId });
  const embedded = embeddedText !== undefined;
  const legacyText = useMemo(
    () => rows?.find((r) => r.annual_cell_key === recordKey && r.kind === kind)?.text ?? '',
    [kind, recordKey, rows]
  );
  const savedText = embedded && (embeddedText ?? '').trim() ? (embeddedText as string) : legacyText;
  /** null = 저장본 그대로 표시 */
  const [draft, setDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const text = draft ?? savedText;
  const isDirty = draft != null && draft !== savedText;
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);
  const stateRef = useRef({ draft, savedText, legacyText });
  stateRef.current = { draft, savedText, legacyText };

  const editorMembers = useMemo(() => {
    const m = new Map<string, ClassMemberRow>();
    [...(allMembers ?? []), ...members].forEach((x) => x?.id && m.set(x.id, x));
    return Array.from(m.values());
  }, [allMembers, members]);

  useEffect(() => {
    abortRef.current?.abort();
    setDraft(null);
    setStatus('idle');
    setError('');
  }, [workspaceId, recordKey, kind]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useImperativeHandle(
    ref,
    () => ({
      persist: async () => {
        const { draft: d, savedText: s } = stateRef.current;
        if (embedded) {
          // 내장 저장은 부모가 기록과 함께 수행(프로그램 표는 표 저장 시점에 DB 반영되므로 레거시 행은 여기서 지우지 않음)
          setDraft(null);
          return;
        }
        if (d == null || d === s) return;
        await saveRecordAiFeedback(workspaceId, recordKey, kind, d);
        setDraft(null);
      },
      capture: () => {
        const { draft: d, savedText: s, legacyText: l } = stateRef.current;
        const ws = workspaceId;
        const key = recordKey;
        const k = kind;
        const emb = embedded;
        return async () => {
          if (emb) {
            if (l) await deleteRecordAiFeedback(ws, key, [k]);
            return;
          }
          if (d == null || d === s) return;
          await saveRecordAiFeedback(ws, key, k, d);
        };
      },
      getEmbedValue: () => {
        const { draft: d, savedText: s } = stateRef.current;
        return clipFeedbackText(d ?? s, 6000);
      },
      remove: async (kinds) => {
        await deleteRecordAiFeedback(workspaceId, recordKey, kinds && kinds.length ? kinds : [kind]);
        setDraft(null);
      },
    }),
    [embedded, kind, recordKey, workspaceId]
  );

  const run = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const anon = anonymizeRecord({ note, contextLines, members, allMembers });
    if (!anon.note) {
      setStatus('error');
      setError('먼저 기록 내용을 입력해 주세요.');
      return;
    }
    setStatus('loading');
    setError('');
    try {
      const result = await requestAiRecordFeedback(kind, anon, ctrl.signal);
      if (ctrl.signal.aborted) return;
      const lines: string[] = [];
      if (result.overall.trim()) lines.push(`(전체피드백) ${result.overall.trim()}`);
      result.students.forEach((s) => {
        // 이 기록에 태그로 등장한 학생 코드만 칩으로 복원(그 외 코드는 버림)
        const id = anon.codes.includes(s.code) ? anon.codeToMember[s.code]?.id : undefined;
        if (!id) return;
        const parts = [
          s.analysis.trim() && `(분석) ${s.analysis.trim()}`,
          s.guidance.trim() && `(피드백) ${s.guidance.trim()}`,
        ].filter(Boolean);
        lines.push(`${buildMemberTagRaw(id)} ${parts.join(' ')}`);
      });
      // 전체 피드백·학생별 피드백 사이 빈 줄 1개
      setDraft(lines.join('\n\n'));
      setStatus('idle');
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setStatus('error');
      setError(e instanceof Error ? e.message : 'AI 피드백을 받지 못했습니다.');
    }
  }, [allMembers, contextLines, kind, members, note]);

  const loading = status === 'loading';
  const hasText = !!text.trim();
  const dirty = draft != null && draft !== savedText;

  return (
    <div className="cp-ai-feedback">
      {loading || status === 'error' || hasText || draft != null ? (
        <div className="cp-ai-feedback-block" aria-live="polite">
          <div className="cp-ai-feedback-block-head">
            <span>분석 및 피드백</span>
            {!loading ? (
              <span className="cp-ai-feedback-block-hint">
                {dirty ? '확인/저장 시 기록과 함께 저장됩니다' : hasText ? '저장됨' : ''}
              </span>
            ) : null}
          </div>
          {loading ? (
            <p className="cp-ai-feedback-loading">
              <span className="cp-ai-feedback-spinner" aria-hidden />
              학생 이름을 가린 뒤 기록을 분석하는 중…
            </p>
          ) : null}
          {status === 'error' ? (
            <p className="cp-ai-feedback-error" role="alert">
              {error}
            </p>
          ) : null}
          {!loading && (hasText || draft != null) ? (
            <ClassRecordMemoNoteEditor
              key={`${recordKey}:${kind}:ai`}
              className="cp-ai-feedback-editor"
              value={text}
              onChange={(v) => setDraft(v)}
              members={editorMembers}
              applyBulkMemberTagRules={false}
              placeholder="AI 피드백"
            />
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        className="cp-ai-feedback-btn"
        disabled={disabled || loading}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => void run()}
      >
        <AiSparkIcon />
        {loading ? 'AI 분석 중…' : hasText ? 'AI 피드백 다시 받기' : 'AI 피드백'}
      </button>
    </div>
  );
});

/** 마스터 관리자에게만 렌더 (서버도 403 으로 재검증) */
/** 피드백 패널 오류가 부모 기록 모달 열기/렌더를 절대 막지 않도록 격리 */
class AiFeedbackErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err: unknown) {
    console.warn('[ai-feedback] panel disabled after error', err);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const AiRecordFeedbackPanel = forwardRef<AiRecordFeedbackPanelHandle, Props>(function AiRecordFeedbackPanel(props, ref) {
  const isMaster = useIsMasterAdmin();
  if (!isMaster) return null;
  return (
    <AiFeedbackErrorBoundary>
      <Inner ref={ref} {...props} />
    </AiFeedbackErrorBoundary>
  );
});

export default AiRecordFeedbackPanel;
